import { ComponentType, ReactNode } from 'react';

import type { SvgIconProps } from 'ui/icons/SvgIcon';
import { NoResult } from 'ui/illustrations/NoResult';
import { Overview } from 'ui/illustrations/Overview';
import { WebLink } from 'ui/theme/typography/components';

import { EmptyStateContainer, EmptyStateDescription, EmptyStateTitle } from './styled';
import { EVENT_HELP_DOC_LINK } from '../../constants';

export enum EmptyStateType {
  newUser = 'newUser',
  filter = 'filter',
  error = 'error',
}

interface EmptyStateSectionProps {
  type: EmptyStateType;
}

type Format = {
  Icon: ComponentType<React.PropsWithChildren<SvgIconProps>>;
  title: string;
  description: ReactNode;
};

export const emptyStateFormats: Record<EmptyStateType, Format> = {
  newUser: {
    Icon: Overview,
    title: 'No events to display!',
    description: (
      <span>
        This contact doesn&apos;t have any event data, in any date range. Check back after
        you&apos;ve sent them some campaigns! In the meantime,{' '}
        <WebLink href={EVENT_HELP_DOC_LINK}>learn more about events</WebLink>.
      </span>
    ),
  },
  filter: {
    Icon: NoResult,
    title: 'No events to display!',
    description: <span>Please try adjusting your filters and try again</span>,
  },
  error: {
    Icon: NoResult,
    title: 'Sorry, we couldn’t load this event summary',
    description: <span>Try refreshing the page. If this keeps happening, let us know.</span>,
  },
};

export const EmptyStateSection = ({ type }: EmptyStateSectionProps) => {
  const { Icon, title, description } = emptyStateFormats[type];

  return (
    <EmptyStateContainer data-test={`empty-state-${type}`}>
      <Icon />
      <EmptyStateTitle>{title}</EmptyStateTitle>
      <EmptyStateDescription>{description}</EmptyStateDescription>
    </EmptyStateContainer>
  );
};
